import React, { useCallback, useState, useRef } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Input } from 'ui-components/components/input';

import IntlProvider from '../../intl/intl-provider';
import AuthenticatedLocaleContextProvider from '../../intl/locale-context-provider/authenticated-locale-context-provider';
import {
  groupHeadingMessages,
  messages as fieldReplacementMessages,
  mainMessages,
  supportedInEmail,
} from './buttons/field-replacement/messages';
import EnglishOnly from './component.english';
import { translateTokens } from './helpers';
import style from './style.module.less';

const calculateValueAfterInsert = (cursorPosition, currentValue, translatedField) => {
  if (cursorPosition === 0) {
    return `${translatedField}${currentValue}`;
  }
  const firstPart = currentValue.slice(0, cursorPosition);
  const secondPart = currentValue.slice(cursorPosition);
  return `${firstPart}${translatedField}${secondPart}`;
};

const InputWithFieldReplacements = ({ value: valueProp, onChange }) => {
  const intl = useIntl();
  const ref = useRef();
  const [value, setValue] = useState(translateTokens(valueProp, intl));
  const [cursorPosition, setCursorPosition] = useState(0);
  const onInputChange = (e) => {
    setValue(e.target.value);
  };
  const onUserInteract = useCallback((e) => {
    setCursorPosition(e.target.selectionStart);
  }, []);

  const onFieldClick = (field) => {
    const translatedField = `[${intl.formatMessage(fieldReplacementMessages[field])}]`;
    const newValue = calculateValueAfterInsert(cursorPosition, value, translatedField);
    setValue(newValue);
    if (ref.current) {
      setTimeout(() => {
        ref.current.focus();
        if (ref.current?.setSelectionRange) {
          const newCursorPosition = cursorPosition + translatedField.length;
          ref.current.setSelectionRange(newCursorPosition, newCursorPosition);
          setCursorPosition(newCursorPosition);
        }
      }, 0);
    }
  };

  return (
    <div className={style.wrapper}>
      <Input
        className={style['input-wrapper']}
        exposeNativeEventInCallbacks
        name='field-replacement-input'
        onChange={onInputChange}
        onKeyUp={onUserInteract}
        onMouseUp={onUserInteract}
        setRef={ref}
        value={value}
      />
      <DropdownMenu>
        <DropdownMenuToggler className={style.toggler} theme='white'>
          <FormattedMessage {...mainMessages.buttonText} />
        </DropdownMenuToggler>
        {Object.keys(supportedInEmail).map((groupKey, index) => {
          return (
            <React.Fragment key={`${groupKey}-${index}`}>
              <div className={style['group-header']}>
                <FormattedMessage {...groupHeadingMessages[groupKey]} />
              </div>
              {supportedInEmail[groupKey].map((messageKey, optIndex) => {
                return (
                  <DropdownMenuItem key={optIndex} onClick={() => onFieldClick(messageKey)}>
                    <FormattedMessage {...fieldReplacementMessages[messageKey]} />
                  </DropdownMenuItem>
                );
              })}
            </React.Fragment>
          );
        })}
      </DropdownMenu>
      <AuthenticatedLocaleContextProvider>
        <IntlProvider>
          <EnglishOnly intlFrom={intl} model={value} onChange={onChange} />
        </IntlProvider>
      </AuthenticatedLocaleContextProvider>
    </div>
  );
};

InputWithFieldReplacements.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default InputWithFieldReplacements;
